// LocationSidebar.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import StageForms from "./StageForms";
import LidarViewer from "./LidarViewer";

const RAW_API_BASE = process.env.REACT_APP_API_BASE || "/api";
const API_BASE = String(RAW_API_BASE).replace(/\/+$/, "");

const styles = `
  .sidebar-container { position: absolute; top: 12px; bottom: 12px; right: 12px; width: 450px; background: #fff; z-index: 1000; border-radius: 12px; box-shadow: -4px 0 25px rgba(0,0,0,0.15); display: flex; flex-direction: column; overflow: hidden; animation: slideIn 0.3s ease-out; }
  @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  .sidebar-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; }
  .sidebar-header { background-color: #212529; background-size: cover; background-position: center; color: white; min-height: 180px; position: relative; display: flex; flex-direction: column; justify-content: flex-end; padding: 20px; transition: background-image 0.3s ease-in-out; }
  .header-overlay { background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.9)); position: absolute; inset: 0; z-index: 1; }
  .input-transparent { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.3); color: white; padding: 5px 10px; border-radius: 4px; width: 100%; }
  .detail-card { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 10px; }
  .detail-label { font-size: 0.65rem; text-transform: uppercase; color: #adb5bd; font-weight: 700; }
  .detail-input { border: none; background: transparent; font-size: 0.95rem; font-weight: 600; color: #212529; width: 100%; outline: none; }
  .step-dot { width: 12px; height: 12px; border-radius: 50%; background: #e9ecef; position: relative; z-index: 2; cursor: pointer; transition: transform 0.2s; border: 2px solid white; box-shadow: 0 0 2px rgba(0,0,0,0.2); }
  .step-dot:hover { transform: scale(1.3); }
  .step-line { flex: 1; height: 2px; background: #e9ecef; margin: 0 -1px; z-index: 1; }
  .drop-zone { border: 2px dashed #dee2e6; border-radius: 8px; padding: 0; height: 100%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; min-height: 80px; }
  .drop-zone:hover { background: #f1f3f5; border-color: #adb5bd; }
  .gallery-thumb { cursor: pointer; transition: transform 0.15s; }
  .gallery-thumb:hover { transform: scale(1.03); }

  .lidar-btn {
    background-color: #0f172a; color: white; border: none;
    border-radius: 8px; padding: 12px; width: 100%;
    font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: background-color 0.2s;
  }
  .lidar-btn:hover { background-color: #1e293b; color: white; }

  .ls-lightbox {
    position: fixed; inset: 0; background: rgba(0,0,0,0.8);
    display: flex; align-items: center; justify-content: center;
    z-index: 2000; padding: 20px;
  }
  .ls-lightbox-content { position: relative; max-width: 50%; max-height: 90%; }
  .ls-lightbox-img { max-width: 100%; max-height: 100%; border-radius: 6px; }

  .ls-lightbox-prev, .ls-lightbox-next, .ls-lightbox-close {
    position: absolute; background: rgba(0,0,0,0.5);
    border: none; padding: 6px 8px; color: white;
    border-radius: 6px; cursor: pointer;
  }
  .ls-lightbox-prev { left: -50px; top: 50%; transform: translateY(-50%); }
  .ls-lightbox-next { right: -50px; top: 50%; transform: translateY(-50%); }
  .ls-lightbox-close { top: -40px; right: 0; }
`;



const LocationSidebar = ({ isOpen, onClose, activeData, onSave }) => {
  const STAGE_CONFIG = [
    { name: "Not Started", color: "#6c757d" },
    { name: "Survey",      color: "#0d6efd" },
    { name: "Valuation",   color: "#0dcaf0" },
    { name: "Negotiation", color: "#6610f2" },
    { name: "Payment",     color: "#fd7e14" },
    { name: "Acquired",    color: "#198754" },
    { name: "Demolished",  color: "#dc3545" },
  ];
  const WORKFLOW_STAGES = STAGE_CONFIG.map(s => s.name);

  const WORKFLOW_ORDER = [
    "Not Started",
    "Survey",
    "Valuation",
    "Negotiation",
    "Payment",
    "Acquired",
    "Demolished",
  ];

  const getNextIncompleteStage = (history = []) => {
    const completed = new Set(
      history
        .map(h => h.stage_name || h.stage)
        .filter(Boolean)
        .map(s => s.toLowerCase())
    );

    return (
      WORKFLOW_ORDER.find(s => !completed.has(s.toLowerCase())) ||
      WORKFLOW_ORDER[WORKFLOW_ORDER.length - 1]
    );
  };


  const initialFormState = {
    uuid: "", building_id: "", address: "", owner_name: "",
    land_area: "", building_area: "", floors: "", email: "",
    organization: "", status: "Not Started", x_coord: "", y_coord: "",
    cover_image_url: "", history: [], files: { general: [], stages: {} }
  };

  const [currentUser, setCurrentUser] = useState(null);
  
  // 1. DETERMINE ROLE
  const isAdmin = currentUser?.role === "admin";

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Stages form state
  const [showStageForm, setShowStageForm] = useState(false);
  const [viewingStageName, setViewingStageName] = useState(null);
  const [stageInputs, setStageInputs] = useState({});
  const [stageFiles, setStageFiles] = useState({});
  const [stageSavedFiles, setStageSavedFiles] = useState({});

  // Attachments
  const [existingDocs, setExistingDocs] = useState([]);
  const [newDocs, setNewDocs] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);
  const [newGallery, setNewGallery] = useState([]);
  const [newGalleryPreviews, setNewGalleryPreviews] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const coverInputRef = useRef(null);
  const docInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const [lidarOpen, setLidarOpen] = useState(false);


  // ---------- Helper functions for file handling ----------
  const isImageUrl = (url) => {
    return /\.(jpe?g|png|gif|webp|tiff?|bmp)$/i.test(url.split("?")[0]);
  };

  const extractFilenameFromUrl = (url) => {
    try {
      const urlPart = url.split("/").pop() || url;
      // remove querystring
      const base = urlPart.split("?")[0];
      // return full file name or fallback trimmed string
      return decodeURIComponent(base);
    } catch (e) {
      return url;
    }
  };

  const friendlyNameFromFilename = (filename) => {
    let name = filename;
    name = name.replace(/^cover-/, "");
    name = name.replace(/^[0-9]{8,}-/, "");
    const parts = name.split("-");
    if (parts.length > 1) {
      const maybeNumber = parts[0];
      if (/^[a-zA-Z_]+$/.test(maybeNumber) === false) {
        name = parts.slice(1).join("-");
      }
    }
    return name;
  };

  const handleDelete = async () => {
    // 1. Checks
    if (!isAdmin) return;
    if (!formData.uuid) return; // Can't delete what hasn't been saved yet

    // 2. Confirmation
    const confirmDelete = window.confirm(
      `Are you sure you want to delete location ${formData.building_id}? \n\nThis will remove all history, files, and data permanently.`
    );
    if (!confirmDelete) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      // 3. API Call
      // Assuming your backend supports DELETE /location/:uuid
      const res = await fetch(`${API_BASE}/location/${formData.uuid}`, {
        method: "DELETE",
        headers: { 
          "Authorization": `Bearer ${token}` 
        }
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to delete location");
      }

      // 4. Cleanup UI
      alert("Location deleted successfully.");
      onClose(); // Close the sidebar
      if (onSave) onSave(); // Trigger parent refresh (e.g., remove marker from map)

    } catch (err) {
      alert("Error deleting: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };


  // --- Fetch Details (includes stage-details fallback) ---
  const fetchLocationDetails = useCallback(async (buildingId) => {
    if (!buildingId) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");

      // 1. Fetch General Location Data
      const res = await fetch(`${API_BASE}/location/${encodeURIComponent(buildingId)}`, {
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
      if (!res.ok) return;
      const data = await res.json();


      const cleanFileUrl = (url) => {
        if (!url || typeof url !== 'string') return "";

        // 1. If URL contains the wrong port (5111), swap it to 3111
        if (url.includes(":5111")) {
             return url.replace(":5111", ":3111");
        }
        
        // 2. If URL is just the IP (default port 80), force port 3111
        if (url.includes("103.163.184.111") && !url.includes(":3111")) {
             return url.replace("103.163.184.111", "103.163.184.111:3111");
        }

        // 3. If it's already relative (starts with /), prepend the API base domain + 3111
        if (url.startsWith("/")) {
             return `http://103.163.184.111:3111${url}`;
        }

        return url;
      };
      // ---------------------------------------------------------

      // --- Check for LiDAR ---
      let lidarExists = false;
      try {
        const lidarRes = await fetch(`${API_BASE}/location/${encodeURIComponent(buildingId)}/lidar`, {
          headers: token ? { "Authorization": `Bearer ${token}` } : {}
        });
        if (lidarRes.ok) {
          const lidarData = await lidarRes.json();
          lidarExists = lidarData.exists;
        }
      } catch (e) {}

      // --- Multi-Tenancy Check ---
      if (currentUser?.organization && data.organization) {
         if (data.organization !== currentUser.organization) {
            setIsLoading(false); return;
         }
      }

      // --- Fetch History ---
      let historyRows = Array.isArray(data.history) ? data.history : [];
      if (data.uuid) {
        try {
          const res2 = await fetch(`${API_BASE}/location/stage-details/${encodeURIComponent(data.uuid)}`, {
            headers: token ? { "Authorization": `Bearer ${token}` } : {}
          });
          if (res2.ok) {
            const tableRows = await res2.json();
            if (Array.isArray(tableRows) && tableRows.length > 0) historyRows = tableRows;
          }
        } catch (e) {}
      }

      const incomingFiles = data.files || { general: [], stages: {} };

      // --- APPLY FIX TO GENERAL FILES ---
      let general = [];
      const processEntry = (entry) => {
          if (!entry) return null;
          if (typeof entry === "string") {
            const cleaned = cleanFileUrl(entry); // <--- Fix applied
            const fname = cleaned.split("/").pop().split("?")[0];
            return { url: cleaned, name: fname };
          } else if (typeof entry === "object") {
            const cleaned = cleanFileUrl(entry.url || entry.path); // <--- Fix applied
            return { 
                url: cleaned, 
                name: entry.name || cleaned?.split("/").pop()?.split("?")[0] || "" 
            };
          }
          return null;
      };

      if (Array.isArray(incomingFiles.general)) {
        general = incomingFiles.general.map(processEntry).filter(Boolean);
      } else if (incomingFiles.general && typeof incomingFiles.general === "object") {
        general = Object.values(incomingFiles.general).map(processEntry).filter(Boolean);
      }

      // --- APPLY FIX TO COVER IMAGE ---
      const coverUrl = cleanFileUrl(data.cover_image_url);

      const isImageUrl = (u = "") => /\.(jpe?g|png|gif|webp|tiff?|bmp|svg)$/i.test((u || "").split("?")[0]);
      const imgs = [];
      const docs = [];
      
      for (const f of general) {
        if (!f || !f.url) continue;
        if (coverUrl && f.url === coverUrl) continue;
        if (f.name && f.name.startsWith("cover-")) continue;
        
        if (isImageUrl(f.url)) imgs.push(f.url);
        else {
          const filename = f.name || f.url.split("/").pop().split("?")[0];
          const friendly = String(filename).replace(/^cover-/, "").replace(/^[0-9]{8,}-/, "");
          docs.push({ name: friendly, url: f.url });
        }
      }

      // --- APPLY FIX TO STAGE FILES ---
      const normalizedStages = {};
      if (incomingFiles.stages && typeof incomingFiles.stages === "object") {
        for (const [stageKey, arr] of Object.entries(incomingFiles.stages || {})) {
          if (!Array.isArray(arr)) continue;
          normalizedStages[stageKey] = arr.map(item => {
            if (!item) return null;
            if (typeof item === "string") {
              const cleaned = cleanFileUrl(item); // <--- Fix applied
              const nm = cleaned.split("/").pop().split("?")[0];
              return { url: cleaned, name: nm, fieldName: nm.split("-")[0] || "" };
            } else if (typeof item === "object") {
              const cleaned = cleanFileUrl(item.url || item.path); // <--- Fix applied
              return { 
                  url: cleaned, 
                  name: item.name || cleaned?.split("/").pop()?.split("?")[0] || "", 
                  fieldName: item.fieldName || (item.name ? item.name.split("-")[0] : "") 
              };
            }
            return null;
          }).filter(Boolean);
        }
      }

      if (lidarExists) {
        if (!normalizedStages['survey']) normalizedStages['survey'] = [];
        normalizedStages['survey'].push({
           name: "3D_Scan_Data.las", fieldName: "lasFile", url: "#lidar-view", isLidar: true 
        });
      }

      setFormData(prev => ({
        ...prev,
        uuid: data.uuid,
        building_id: data.building_id,
        address: data.address || "",
        owner_name: data.owner_name || "",
        land_area: data.land_area || "",
        building_area: data.building_area || "",
        floors: data.floors || "",
        status: data.current_status || data.status || "Not Started",
        cover_image_url: coverUrl, // <--- Using clean URL
        x_coord: data.x_coord ? String(data.x_coord) : prev.x_coord,
        y_coord: data.y_coord ? String(data.y_coord) : prev.y_coord,
        history: historyRows || [],
        files: { general, stages: normalizedStages }
      }));

      setExistingGallery(imgs);
      setExistingDocs(docs);

    } catch (err) {
      console.error("Error fetching details:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);


  // --- Effects ---
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/me`, { headers: { "Authorization": `Bearer ${token}` } });
        if (res.ok) setCurrentUser(await res.json());
      } catch (e) {}
    };
    fetchUser();
  }, []);

  const currentUuidRef = useRef(null);

  // --- 1. UPDATE FORM DATA WHEN PROPS CHANGE (Map Selection or Drag) ---
  useEffect(() => {
    if (!activeData) return;

    // Check if we are just dragging the pin on the SAME location
    const isSameLocation = activeData.uuid && (activeData.uuid === currentUuidRef.current);

    if (isSameLocation) {
      // SCENARIO A: DRAGGING
      // Just update the coordinates in the form state. 
      // CRITICAL: Do NOT reset files or fetch API (this prevents snapping back)
      setFormData(prev => ({
        ...prev,
        x_coord: String(activeData.x_coord || activeData.lat || prev.x_coord),
        y_coord: String(activeData.y_coord || activeData.lng || prev.y_coord)
      }));
      return; // Stop here
    }

    // SCENARIO B: SWITCHING TO A NEW LOCATION
    currentUuidRef.current = activeData.uuid; // Update the ref

    // Reset file states
    setExistingDocs([]); setExistingGallery([]); setNewDocs([]);
    setNewGallery([]); setNewGalleryPreviews([]); setCoverFile(null); setCoverPreview(null);
    setShowStageForm(false); setViewingStageName(null);

    if (activeData.uuid || activeData.building_id) {
      // Existing Location Selected
      setFormData({
        ...initialFormState,
        ...activeData,
        status: activeData.current_status || activeData.status || "Not Started",
        x_coord: String(activeData.x_coord || activeData.lat || ""),
        y_coord: String(activeData.y_coord || activeData.lng || "")
      });
      // Only fetch details if it's a fresh selection, not a drag
      if (activeData.building_id) fetchLocationDetails(activeData.building_id);
    } 
    else if (activeData.lat && activeData.lng) {
      // New Pin Dropped (Add Mode)
      setFormData({
        ...initialFormState,
        ...activeData,
        x_coord: String(activeData.lat), 
        y_coord: String(activeData.lng),
        email: currentUser?.email || "", 
        organization: currentUser?.organization || ""
      });
    }
  }, [activeData, currentUser, fetchLocationDetails]);

  // --- Helpers ---
  const toStageKey = (stageName) => String(stageName || "").toLowerCase().replace(/\s+/g, '_');

  const normalizeHistoryRow = (row = {}) => {
    const copy = { ...row };
    const dateFields = ['survey_date', 'payment_date', 'planned_demo_date', 'created_at', 'updated_at'];
    dateFields.forEach(f => {
      if (copy[f]) {
        try {
          const d = new Date(copy[f]);
          if (!Number.isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            copy[f] = `${yyyy}-${mm}-${dd}`;
          }
        } catch (e) { /* ignore */ }
      }
    });
    return copy;
  };

  const matchesStage = (row, stageName) => {
    if (!row || !stageName) return false;
    const target = String(stageName).toLowerCase();
    const candidates = ['stage_name', 'stage', 'stageName', 'stage_title'];
    for (const k of candidates) {
      if (Object.prototype.hasOwnProperty.call(row, k) && row[k] != null) {
        if (String(row[k]).toLowerCase() === target) return true;
      }
    }
    for (const [, v] of Object.entries(row)) {
      if (typeof v === 'string' && v.toLowerCase() === target) return true;
    }
    return false;
  };

  const isAllowedToViewStage = (stageName) => {
    const currentIdx = WORKFLOW_STAGES.indexOf(formData.status);
    const targetIdx = WORKFLOW_STAGES.indexOf(stageName);
    if (targetIdx === -1) return false;
    return targetIdx <= currentIdx + 1;
  };

  const handleDotClick = (stageName) => {
    if (!isAllowedToViewStage(stageName)) {
      alert("You can only open the current stage or the next stage in the workflow.");
      return;
    }

    setViewingStageName(stageName);
    setStageInputs({});
    setStageSavedFiles({});
    setStageFiles({});

    const hist = Array.isArray(formData.history) ? formData.history : [];
    const matches = hist.filter(r => matchesStage(r, stageName));

    let chosen = null;
    if (matches.length === 1) chosen = matches[0];
    else if (matches.length > 1) {
      const withId = matches.filter(m => typeof m.id === 'number' || (typeof m.id === 'string' && /^\d+$/.test(m.id)));
      if (withId.length > 0) chosen = withId.sort((a,b) => Number(b.id) - Number(a.id))[0];
      else {
        const withDate = matches.filter(m => m.created_at);
        if (withDate.length > 0) chosen = withDate.sort((a,b) => new Date(b.created_at) - new Date(a.created_at))[0];
        else chosen = matches[matches.length - 1];
      }
    }

    // --- NEW: Helper to map AND CLEAN files for the stage form ---
    const mapStageFiles = (filesList) => {
        const fileMap = {};
        (filesList || []).forEach(f => {
            // Apply URL Cleaner logic here too
            const cleanUrl = (f.url && f.url.includes("103.163.184.111")) 
                ? f.url.replace(/^https?:\/\/[^/]+/, "") 
                : f.url;

            if (f.fieldName) fileMap[f.fieldName] = cleanUrl;
            else if (f.name) {
              const extracted = f.name.split('-')[0];
              if (extracted) fileMap[extracted] = cleanUrl;
            } else if (typeof f === 'string') {
               const cUrl = f.includes("103.163.184.111") ? f.replace(/^https?:\/\/[^/]+/, "") : f;
               const base = cUrl.split('/').pop();
               const extless = base.split('-')[0];
               fileMap[extless] = cUrl;
            }
        });
        return fileMap;
    };

    const stageKey = toStageKey(stageName);
    const storedFiles = formData.files?.stages?.[stageKey] || [];

    if (!chosen) {
      setStageSavedFiles(mapStageFiles(storedFiles)); // <--- Use helper
      setShowStageForm(true);
      return;
    }

    const normalized = normalizeHistoryRow(chosen);
    setStageInputs(normalized);
    setStageSavedFiles(mapStageFiles(storedFiles)); // <--- Use helper
    setShowStageForm(true);
  };

  const confirmStageUpdate = async () => {
    // SECURITY GUARD
    if (!isAdmin) {
        alert("You do not have permission to update stages.");
        return;
    }

    if (!formData.uuid) return alert("Please save the location (General Info) first.");
    setIsSaving(true);
    setMessage(null);
    try {
      const stageToSave = viewingStageName || formData.status;
      const fd = new FormData();

      fd.append("location_uuid", formData.uuid);
      fd.append("building_id", formData.building_id);
      fd.append("status", stageToSave);

      if (stageInputs && (stageInputs.id || stageInputs.uuid || stageInputs.row_id)) {
        const providedId = stageInputs.id || stageInputs.row_id || stageInputs.uuid;
        const idVal = Array.isArray(providedId) ? providedId[0] : providedId;
        fd.append("id", String(idVal));
      }

      const reserved = new Set(['location_uuid', 'building_id', 'status', 'id', 'uuid', 'created_at', 'updated_at']);
      Object.keys(stageInputs || {}).forEach(key => {
        if (reserved.has(key)) return;
        const v = stageInputs[key];
        if (v === undefined || v === null) return;
        const valToAppend = Array.isArray(v) ? v[0] : v;
        fd.append(key, String(valToAppend));
      });

      Object.keys(stageFiles || {}).forEach(key => {
        const f = stageFiles[key];
        if (!f) return;
        fd.append(key, f, f.name);
      });

      for (const pair of fd.entries()) console.debug("[FORMDATA]", pair[0], pair[1]);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/location/stage`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: fd
      });

      if (!res.ok) {
        let txt = await res.text();
        try { const j = JSON.parse(txt); txt = j.message || JSON.stringify(j); } catch(e){}
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const result = await res.json();
      setMessage(result.message || `Saved ${stageToSave}`);

      if (stageToSave === "Not Started") {
        try {
          const advRes = await fetch(`${API_BASE}/location/advance`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ location_uuid: formData.uuid, building_id: formData.building_id })
          });
          if (advRes.ok) {
            const advJson = await advRes.json();
            await fetchLocationDetails(formData.building_id);
            setFormData(prev => ({ ...prev, status: advJson.location?.current_status || prev.status }));
          } else {
            await fetchLocationDetails(formData.building_id);
          }
        } catch (e) {
          console.warn("Advance request error", e);
          await fetchLocationDetails(formData.building_id);
        }
      } else {
        await fetchLocationDetails(formData.building_id);
      }

      setShowStageForm(false);
      setStageInputs({});
      setStageFiles({});
      setViewingStageName(null);
      setShowStageForm(false);
      setStageInputs({});
      setStageFiles({});
    } catch (err) {
      alert("Failed: " + (err.message || err));
      console.error("confirmStageUpdate error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const initiateNextStep = () => {
    // SECURITY GUARD
    if (!isAdmin) return; 

    const nextStage = getNextIncompleteStage(formData.history || []);

    if (!nextStage) {
      alert("All stages are completed.");
      return;
    }

    handleDotClick(nextStage);
  };


  const handleInputChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleStageInputChange = (e) => setStageInputs(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleStageFileUpload = (key, file) => setStageFiles(p => ({ ...p, [key]: file }));

  const currentStageIndex = WORKFLOW_STAGES.indexOf(formData.status);
  const activeColor = STAGE_CONFIG.find(s => s.name === formData.status)?.color || "#0d6efd";

  const handleCoverUpload = (e) => {
    const f = e.target.files[0];
    if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }
  };
  const handleDocUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) setNewDocs(prev => [...prev, ...files]);
  };
  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) {
      setNewGallery(prev => [...prev, ...files]);
      setNewGalleryPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
    }
  };

  const headerBgUrl = coverPreview ? coverPreview : formData.cover_image_url ? formData.cover_image_url : null;

  const previewImages = useMemo(() => {
    return [...(existingGallery || []), ...(newGalleryPreviews || [])];
  }, [existingGallery, newGalleryPreviews]);

  const openPreview = (index) => {
    if (!previewImages.length) return;
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const closePreview = () => setPreviewOpen(false);

  const showPrev = () => {
    setPreviewIndex(i => (i <= 0 ? previewImages.length - 1 : i - 1));
  };

  const showNext = () => {
    setPreviewIndex(i => (i >= previewImages.length - 1 ? 0 : i + 1));
  };

  useEffect(() => {
    if (!previewOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [previewOpen, previewImages.length]);




  if (!isOpen) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="sidebar-container">
        <div
          className="sidebar-header"
          style={{
            backgroundImage: headerBgUrl ? `url("${headerBgUrl}")` : 'none',
            backgroundColor: '#212529'
          }}
        >
          <div className="header-overlay"></div>
          <button onClick={onClose} className="btn btn-sm btn-light border-0 shadow position-absolute top-0 end-0 m-3 z-3 rounded-circle" style={{ width: 32, height: 32 }}><i className="bi bi-x-lg"></i></button>
          
          {/* ONLY ADMIN CAN UPLOAD COVER */}
          {isAdmin && (
            <>
                <button className="btn btn-dark btn-sm bg-opacity-50 border-secondary position-absolute top-0 start-0 m-3 z-3" onClick={() => coverInputRef.current.click()}><i className="bi bi-camera"></i></button>
                <input type="file" ref={coverInputRef} hidden accept="image/*" onChange={handleCoverUpload} />
            </>
          )}

          <div className="position-relative z-2">
            <div className="badge mb-2" style={{ backgroundColor: activeColor }}>{formData.status}</div>
            
            {/* READONLY FOR NON ADMIN */}
            <input 
                type="text" 
                name="building_id" 
                className="input-transparent fs-2 fw-bold" 
                placeholder="BLD-XXXX" 
                value={formData.building_id} 
                onChange={handleInputChange} 
                readOnly={!isAdmin}
                onBlur={() => { if (formData.building_id) fetchLocationDetails(formData.building_id); }} 
            />
            <div className="text-white-50 small mt-1"><i className="bi bi-geo-alt me-1"></i> {Number(formData.x_coord || 0).toFixed(5)}, {Number(formData.y_coord || 0).toFixed(5)}</div>
          </div>
        </div>

        <div className="sidebar-scroll p-3">
          <div className="mb-4">
            <label className="text-muted small fw-bold mb-1">PROPERTY ADDRESS</label>
            <textarea 
                name="address" 
                className="form-control fw-bold border-0 bg-light" 
                rows="2" 
                placeholder="Enter Property Address..." 
                value={formData.address} 
                onChange={handleInputChange} 
                readOnly={!isAdmin}
            />
          </div>

          {/* --- LIDAR BUTTON CONTAINER --- */}
          <div className="mb-4">
            <button 
                className="lidar-btn" 
                onClick={() => setLidarOpen(true)} // Open local state
                disabled={!formData.building_id}
            >
                <i className="bi bi-box-seam"></i> View 3D Point Cloud
            </button>
          </div>

          <div className="row g-2 mb-4">
            <div className="col-6"><div className="detail-card"><div className="detail-label">Owner</div><input name="owner_name" className="detail-input" placeholder="Unknown" value={formData.owner_name} onChange={handleInputChange} readOnly={!isAdmin} /></div></div>
            <div className="col-6"><div className="detail-card"><div className="detail-label">Floors</div><input name="floors" type="number" className="detail-input" placeholder="0" value={formData.floors} onChange={handleInputChange} readOnly={!isAdmin} /></div></div>
            <div className="col-6"><div className="detail-card"><div className="detail-label">Land (m²)</div><input name="land_area" type="number" className="detail-input" placeholder="0" value={formData.land_area} onChange={handleInputChange} readOnly={!isAdmin} /></div></div>
            <div className="col-6"><div className="detail-card"><div className="detail-label">Bld (m²)</div><input name="building_area" type="number" className="detail-input" placeholder="0" value={formData.building_area} onChange={handleInputChange} readOnly={!isAdmin} /></div></div>
          </div>

          <h6 className="fw-bold small text-muted mb-3 text-uppercase">Acquisition Status</h6>
          <div className="d-flex align-items-center mb-4 px-1 justify-content-between">
            {STAGE_CONFIG.map((stage, index) => {
              const isActive = index <= currentStageIndex;
              const isViewing = viewingStageName === stage.name;
              const allowed = isAllowedToViewStage(stage.name);
              return (
                <React.Fragment key={stage.name}>
                  <div
                    className="step-dot"
                    onClick={() => allowed ? handleDotClick(stage.name) : null}
                    title={allowed ? stage.name : "Locked (complete previous steps to unlock)"}
                    style={{
                      backgroundColor: isActive ? stage.color : '#e9ecef',
                      transform: isViewing ? 'scale(1.5)' : 'scale(1)',
                      borderColor: isViewing ? '#343a40' : 'white',
                      opacity: allowed ? 1 : 0.45,
                      cursor: allowed ? 'pointer' : 'not-allowed',
                      pointerEvents: allowed ? 'auto' : 'none'
                    }}
                  ></div>
                  {index < STAGE_CONFIG.length - 1 && (
                    <div className="step-line" style={{ backgroundColor: index < currentStageIndex ? STAGE_CONFIG[index].color : '#e9ecef' }}></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="card shadow-sm border-0 mb-4">
            {!showStageForm ? (
              <div className="p-3 border rounded bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div><span className="fw-bold d-block" style={{ color: activeColor }}>{formData.status}</span></div>
                  {/* ONLY ADMIN CAN ADVANCE STAGE */}
                  {isAdmin && (
                    <button className="btn btn-sm text-white fw-bold" style={{ backgroundColor: activeColor }} onClick={initiateNextStep} disabled={currentStageIndex === WORKFLOW_STAGES.length - 1 || !formData.uuid}>
                        {formData.uuid ? (formData.status === "Not Started" ? "Start Process" : "Complete Stage") : "Save First"} <i className="bi bi-arrow-right"></i>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <StageForms
                status={viewingStageName || formData.status}
                inputs={stageInputs}
                onInputChange={handleStageInputChange}
                files={stageFiles}
                savedFiles={stageSavedFiles}
                onFileUpload={handleStageFileUpload}
                onCancel={() => { setShowStageForm(false); setViewingStageName(null); }}
                onConfirm={confirmStageUpdate}
                isReadOnly={!isAdmin} // Pass ReadOnly prop
              />
            )}
          </div>

          <hr className="my-4 text-muted opacity-25" />

          {/* Documents */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold small text-muted text-uppercase mb-0">Documents</h6>
            {isAdmin && <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => docInputRef.current.click()}>+ Add Doc</button>}
          </div>
          <div className="d-flex gap-2 mb-4 overflow-auto pb-2">
            {existingDocs.map((d, i) => (
              <a key={`ex-doc-${i}`} href={d.url} target="_blank" rel="noreferrer" className="text-decoration-none">
                <div className="border rounded px-2 py-2 bg-light d-flex align-items-center flex-shrink-0" style={{ width: 130 }}>
                  <i className="bi bi-file-earmark-check fs-5 me-2 text-dark opacity-50"></i>
                  <div className="overflow-hidden"><span className="small fw-bold text-dark d-block text-truncate">{d.name}</span><span className="text-muted" style={{ fontSize: 9 }}>Existing</span></div>
                </div>
              </a>
            ))}
            {newDocs.map((d, i) => (
              <div key={`new-doc-${i}`} className="border border-primary bg-primary bg-opacity-10 rounded px-2 py-2 d-flex align-items-center flex-shrink-0" style={{ width: 130 }}>
                <i className="bi bi-file-earmark-plus fs-5 me-2 text-primary"></i>
                <div className="overflow-hidden"><span className="small fw-bold text-primary d-block text-truncate">{d.name}</span><span className="text-primary opacity-75" style={{ fontSize: 9 }}>Pending Save</span></div>
              </div>
            ))}
            {existingDocs.length === 0 && newDocs.length === 0 && <div className="text-muted small fst-italic">No documents attached</div>}
          </div>
          {isAdmin && <input type="file" ref={docInputRef} hidden multiple onChange={handleDocUpload} />}

          {/* Gallery */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold small text-muted text-uppercase mb-0">Gallery</h6>
            {isAdmin && <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => galleryInputRef.current.click()}>+ Add Image</button>}
          </div>
          <div className="row g-2">
            {existingGallery.map((src, i) => (
              <div className="col-3" key={`ex-img-${i}`}><img
                src={src}
                className="img-fluid rounded border gallery-thumb"
                style={{ aspectRatio: "1/1", objectFit: "cover", width: "100%" }}
                onClick={() => openPreview(i)}
              />
              </div>
            ))}
            {newGalleryPreviews.map((src, i) => (
              <div className="col-3" key={`new-img-${i}`}><div className="position-relative"><img src={src} className="img-fluid rounded border border-primary" style={{ aspectRatio: "1/1", objectFit: "cover", width: "100%" }} alt="Preview" /><div className="position-absolute bottom-0 start-0 end-0 bg-primary text-white text-center py-0" style={{ fontSize: 8 }}>NEW</div></div></div>
            ))}
            {isAdmin && (
                <div className="col-3"><div className="drop-zone rounded" onClick={() => galleryInputRef.current.click()} style={{ aspectRatio: "1/1" }}><i className="bi bi-plus-lg fs-4 text-muted"></i></div></div>
            )}
          </div>
          {isAdmin && <input type="file" ref={galleryInputRef} hidden multiple accept="image/*" onChange={handleGalleryUpload} />}
          <div style={{ height: 60 }}></div>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="border-top bg-white p-3 d-flex gap-2">
          
          {/* [NEW] DELETE BUTTON - Only for Admins & if UUID exists */}
          {isAdmin && formData.uuid && (
            <button 
              className="btn btn-outline-danger"
              onClick={handleDelete}
              disabled={isSaving}
              title="Delete Location"
            >
              <i className="bi bi-trash"></i>
            </button>
          )}

          {/* EXISTING CANCEL/CLOSE BUTTON */}
          <button className="btn btn-light flex-fill" onClick={onClose} disabled={isSaving}>
            {isAdmin ? "Cancel" : "Close"}
          </button>

          {/* EXISTING SAVE BUTTON */}
          {isAdmin && (
            <button 
              className="btn btn-primary flex-fill fw-bold" 
              onClick={async () => {
                if (!formData.building_id) return alert("Building ID is required!");
                setIsSaving(true);
                try {
                  const data = new FormData();
                  
                  // This loop automatically grabs the updated x_coord/y_coord from state
                  Object.keys(formData).forEach(key => {
                    if (key !== 'history' && key !== 'files') {
                        data.append(key, formData[key] || "");
                    }
                  });
                  
                  if (coverFile) data.append("cover_image", coverFile);
                  newGallery.forEach(file => data.append("gallery", file));
                  newDocs.forEach(file => data.append("documents", file));
                  
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${API_BASE}/location/general`, {
                    method: "POST", headers: { "Authorization": `Bearer ${token}` }, body: data
                  });
                  
                  const result = await res.json();
                  if (!res.ok) throw new Error(result.message);
                  
                  // Update UUID if this was a new creation
                  if (result.uuid) {
                      setFormData(prev => ({ ...prev, uuid: result.uuid }));
                      currentUuidRef.current = result.uuid; // Sync ref
                  }

                  setNewDocs([]); setNewGallery([]); setNewGalleryPreviews([]); setCoverFile(null);
                  await fetchLocationDetails(formData.building_id);
                  
                  // This tells Map.js to refresh the dots
                  if (onSave) onSave(result);

                  alert("Location saved successfully!"); // Visual confirmation

                } catch (err) { 
                  alert("Error saving: " + err.message); 
                } finally { 
                  setIsSaving(false); 
                }
              }} 
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Location"}
            </button>
            )}
        </div>
      </div>

      {lidarOpen && (
        <LidarViewer 
            buildingId={formData.building_id} 
            organization={formData.organization} 
            onClose={() => setLidarOpen(false)} 
        />
      )}
      {previewOpen && previewImages.length > 0 && (
        <div className="ls-lightbox" onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}>
          <div className="ls-lightbox-content">
            <button className="ls-lightbox-prev" onClick={(e) => { e.stopPropagation(); showPrev(); }}>
              <i className="bi bi-chevron-left"></i>
            </button>

            <img
              src={previewImages[previewIndex]}
              className="ls-lightbox-img"
              onClick={(e) => e.stopPropagation()}
            />

            <button className="ls-lightbox-next" onClick={(e) => { e.stopPropagation(); showNext(); }}>
              <i className="bi bi-chevron-right"></i>
            </button>

            <button className="ls-lightbox-close" onClick={(e) => { e.stopPropagation(); closePreview(); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationSidebar;